import React, { useState, useEffect } from "react";
import {
  Heart,
  Thermometer,
  Activity,
  Scale,
  Gauge,
  TrendingUp,
  Ruler,
  Circle,
  Calculator,
  Wind,
  AlertCircle,
  Droplets,
  Plus,
  Clock,
  ChevronDown,
  ChevronUp,
  Image as ImageIconLucide,
  Brain,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import { VitalsDetailModal } from "./VitalsDetailMotal";
import { VitalTypeSelectorModal, VitalsModal } from "./VitalsModal";
import {
  vitalTypes,
  usePatientVitalsState,
  getVitalKey,
} from "@/lib/dashboard/vitalsData";
import { getPatientVitalsCharts } from "@/dispatchers/patientDispatchers";
import useTitanApp from "@/hooks/useTitanApp";
import dayjs from "dayjs";
import { ethnicityTypes } from "../../../enum/ethnicityType";
import { UploadImageModal } from "@/components/PatientConsultation/components/UploadImageModal";
import { ImageVitalsDetailModal } from "./ImageVitalsDetailModal";
import { NEWS2ScorePanelCompact } from "./NEWS2ScorePanelCompact";
import {
  setCurrentOpenVitalsModal,
  setCurrentOpenDeleteVitalsModal,
  addPatientVitals,
  updateConsultationActions,
  SaveAIChatMessage,
  setVitalsDetails,
  setIncrementVitalsCompleted,
  getPatientVitals,
  GetPatientVitalsSummaryByPatientId,
  getPatientConsultationByConsultationId,
} from "@/dispatchers/patientDispatchers";
import { GoPlusCircle } from "react-icons/go";
import { ActionType } from "@/enum/ActionType";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ImageVitalReading {
  id: string;
  type: "image";
  label: string; // body area (e.g. "Face", "Left Knee")
  description: string;
  imagePreview: string;
  fileName?: string;
  timestamp: Date;
  isVideo?: boolean;
  videoUrl?: string;
}

const CustomTooltip = ({ active, payload, label, vital }) => {
  if (vital) {
    return (
      <div className="rounded-lg bg-white/90 dark:bg-gray-800 p-2 shadow-md text-xs">
        <div className="font-semibold">{vital.label}</div>
        <div>
          {vital.value} {vital.unit}
        </div>
        {vital.createdOn && (
          <div className="text-gray-500">
            {dayjs(vital.createdOn).format("DD MMM, HH:mm")}
          </div>
        )}
      </div>
    );
  }
  return null;
};

interface VitalReading {
  id: string;
  type: string;
  value: string;
  unit: string;
  label: string;
  description: string;
  timestamp: Date;
  historicalData: Array<{
    date: string;
    value: number;
  }>;
}

interface VitalsSectionProps {
  vitals: VitalReading[];
  isDarkMode?: boolean;
  onSaveVitals?: (vitalData: any) => void;
  /** Pass patient context for the upload modal (needed later for API work) */
  patient?: {
    consultationId?: string;
    patientId?: string;
    email?: string;
    name?: string;
    [key: string]: any;
  };
  compact?: boolean;
}

// ─── Helper maps ──────────────────────────────────────────────────────────────

const getVitalIcon = (type: string) => {
  switch (type) {
    case "heart-rate":
      return Heart;
    case "temperature":
      return Thermometer;
    case "blood-pressure":
      return Activity;
    case "weight":
      return Scale;
    case "respiratory-rate":
      return Wind;
    case "news-pain-score":
      return AlertCircle;
    case "height":
      return Ruler;
    case "circumference":
      return Circle;
    case "bmi":
      return Calculator;
    case "oxygen-saturation":
      return Gauge;
    case "blood-sugar":
      return Droplets;
    case "consciousness-level":
      return Brain;
    case "supplemental-oxygen":
      return Wind;
    default:
      return Activity;
  }
};

const getVitalColor = (type: string) => {
  switch (type) {
    case "heart-rate":
      return "#ef4444";
    case "temperature":
      return "#f97316";
    case "blood-pressure":
      return "#8b5cf6";
    case "weight":
      return "#10b981";
    case "oxygen-saturation":
      return "#06b6d4";
    case "respiratory-rate":
      return "#22c55e";
    case "news-pain-score":
      return "#f97316";
    case "blood-sugar":
      return "#e11d48";
    case "height":
      return "#3b82f6";
    case "circumference":
      return "#8b5cf6";
    case "bmi":
      return "#f59e0b";
    default:
      return "#6b7280";
  }
};

const getVitalThresholds = (type: string) => {
  switch (type) {
    case "heart-rate":
      return { low: 60, high: 100, unit: "BPM" };
    case "temperature":
      return { low: 36.1, high: 37.2, unit: "°C" };
    case "blood-pressure":
      return { low: 90, high: 140, unit: "mmHg" };
    case "weight":
      return { low: 50, high: 100, unit: "kg" };
    case "bmi":
      return { low: 18.5, high: 24.9, unit: "kg/m²" };
    case "oxygen-saturation":
      return { low: 95, high: 100, unit: "%" };
    case "blood-sugar":
      return { low: 4.0, high: 10.0, unit: "mmol/L" };
    case "news-pain-score":
      return { low: 0, high: 3, unit: "/10" };
    default:
      return { low: 0, high: 100, unit: "" };
  }
};

// ─── Main Component ───────────────────────────────────────────────────────────

export function VitalsSection({
  vitals,
  isDarkMode = false,
  onSaveVitals,
  patient,
  compact = false,
}: VitalsSectionProps) {
  // ── Standard vital state ──
  const [selectedVital, setSelectedVital] = useState<VitalReading | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartData, setChartData] = useState<any | null>(null);

  // ── Image vital state ──
  const [imageVitals, setImageVitals] = useState<ImageVitalReading[]>([]);
  const [selectedImageVital, setSelectedImageVital] =
    useState<ImageVitalReading | null>(null);
  const [isImageDetailOpen, setIsImageDetailOpen] = useState(false);

  const { patientID } = usePatientVitalsState();
  const { patientState, appState } = useTitanApp();
  const { features } = appState || {};
  const { ifNews2Enabled } = features || {};
  const {
    patientVitals,
    selectedPatientDetails,
    consultationId,
    consultationData,
    selectedPatient,
  } = patientState || {};
  const handleCompactVitalSave = async (vitalData: any) => {
    // Keep original flow running (dashboard update, addNewPatientVital in parent, etc.)
    if (onSaveVitals) onSaveVitals(vitalData);

    // Only fire the actions panel flow when there's an active consultation
    if (!consultationId) return;

    const recordedAt = new Date().toISOString();
    const { type, data } = vitalData || {};
    if (!type || !data) return;

    const vitalLabelMap: Record<string, string> = {
      "heart-rate": "Heart Rate",
      "blood-pressure": "Blood Pressure",
      temperature: "Temperature",
      weight: "Weight",
      height: "Height",
      "respiratory-rate": "Respiratory Rate",
      bmi: "BMI Calculator",
      circumference: "Circumference",
      "oxygen-saturation": "Oxygen Saturation",
      "news-pain-score": "NEWS Pain Score",
      "blood-sugar": "Blood Sugar",
      "consciousness-level": "Consciousness (AVPU)",
      "supplemental-oxygen": "Supplemental Oxygen",
    };

    const obj: any = {
      patientId: String(selectedPatientDetails?.patientID || ""),
      patientName: String(selectedPatient || ""),
      consultationId,
      recordedAt,
      vitalType: vitalLabelMap[type] || type,
      vitalTypeKey: type.replace(/-/g, ""),
      serviceName: consultationData?.serviceName,
    };

    switch (type) {
      case "heart-rate":
        obj.heartRate = {
          Value: String(data.value || ""),
          Unit: "BPM",
          recordedAt,
        };
        break;
      case "blood-pressure":
        obj.bloodPressure = {
          Systolic: {
            Value: String(data.systolic || ""),
            Unit: "mmHg",
            recordedAt,
          },
          Diastolic: {
            Value: String(data.diastolic || ""),
            Unit: "mmHg",
            recordedAt,
          },
          recordedAt,
        };
        break;
      case "temperature":
        obj.temperature = {
          Value: String(data.value || ""),
          Unit: String(data.unit || "°C"),
          recordedAt,
        };
        break;
      case "weight":
        obj.weight = {
          Value: String(data.value || ""),
          Unit: String(data.unit || "kg"),
          recordedAt,
        };
        break;
      case "height":
        obj.height = {
          Value: String(data.value || ""),
          Unit: String(data.unit || "cm"),
          recordedAt,
        };
        break;
      case "respiratory-rate":
        obj.respiratoryRate = {
          Value: String(data.value || ""),
          Unit: "breaths/min",
          recordedAt,
        };
        break;
      case "oxygen-saturation":
        obj.OxygenSaturation = {
          Value: String(data.value || ""),
          Unit: "%",
          recordedAt,
        };
        break;
      case "news-pain-score":
        obj.PainScore = {
          Value: String(data.value || ""),
          Unit: "/10",
          recordedAt,
        };
        break;
      case "blood-sugar":
        obj.GlucoseLevel = {
          Value: String(data.value || ""),
          Unit: String(data.unit || "mmol/L"),
          recordedAt,
        };
        break;
      case "consciousness-level":
        obj.Consciousness = {
          Value: String(data.value || "Alert"),
          Unit: "",
          recordedAt,
        };
        break;
      case "supplemental-oxygen":
        obj.SupplementalOxygen = {
          Value: String(data.value || "No"),
          Unit: "",
          recordedAt,
        };
        break;
      default:
        break;
    }

    const vitalsPayload = {
      payload: JSON.stringify({
        vitalsDetails: obj,
        timestamp: recordedAt,
        actionPerformedAt: recordedAt,
      }),
      actionName: ActionType[5],
      actionNumber: "5",
      isPerformed: true,
      performedAt: recordedAt,
    };

    try {
      const response = await addPatientVitals(obj);
      await updateConsultationActions({
        consultationId,
        payload: vitalsPayload,
      });
      await getPatientConsultationByConsultationId(consultationId);
      await SaveAIChatMessage({
        ConsultationChat: JSON.stringify([
          {
            sender: "success",
            message: "Vitals completed successfully.",
            actionId: 5,
            details: vitalsPayload,
          },
        ]),
        ConsultationId: consultationId,
      });
      setVitalsDetails(vitalsPayload);
      if (response) setIncrementVitalsCompleted(true);
      await getPatientVitals(selectedPatientDetails?.patientID);
      await GetPatientVitalsSummaryByPatientId(
        selectedPatientDetails?.patientID,
      );
    } catch (error) {
      console.error("Error saving vital from compact VitalsSection:", error);
    }
  };
  // ── Hydrate image vitals from API response ─────────────────────────────────
  // imageVitals[] comes back from getPatientVitals → patientVitals.imageVitals
  // Each entry has: bodyArea, value (file path or base64), fileName, fileExtension, contentType, createdOn
  // We group by bodyArea in imageVitalsByArea (below) — one card per area, most recent as thumbnail
  useEffect(() => {
    const apiImageVitals = (patientVitals as any)?.imageVitals;
    // Always overwrite — clears stale images when switching to a patient with none
    if (!Array.isArray(apiImageVitals) || apiImageVitals.length === 0) {
      setImageVitals([]);
      return;
    }

    const hydrated: ImageVitalReading[] = apiImageVitals.map((iv: any) => ({
      id: iv.documentId ?? `${iv.bodyArea}-${iv.createdOn}`,
      type: "image" as const,
      label: iv.bodyArea,
      description: `Image of ${iv.bodyArea}`,
      // value is a file path after backend saves it; if it somehow still arrives as raw base64 add the prefix
      imagePreview: iv.value?.startsWith("data:")
        ? iv.value
        : iv.value?.startsWith("/9j") || iv.value?.startsWith("iVBOR")
          ? `data:${iv.contentType ?? "image/jpeg"};base64,${iv.value}`
          : iv.value, // it's a file path/URL — use as-is
      fileName: iv.fileName,
      timestamp: new Date(iv.createdOn),
      isVideo: false,
    }));

    setImageVitals(hydrated);
  }, [patientVitals]);

  const VITALS_PER_ROW = compact ? 4 : 5;

  // ── Save handler for image upload ──────────────────────────────────────────
  // NOTE: No optimistic add here — UploadImageModal calls getPatientVitals after
  // saving, which triggers the useEffect above to hydrate imageVitals from the API.
  // Adding optimistically AND hydrating causes duplicates.
  const handleSaveImageVital = (data: {
    title: string;
    imagePreview?: string;
    fileName?: string;
    isVideo?: boolean;
    consultationId?: string;
  }) => {
    if (!data.imagePreview) return;
    // Bubble up to parent only (no local state mutation)
    if (onSaveVitals) {
      onSaveVitals({
        type: "image",
        data: {
          value: data.title,
          unit: "",
          imagePreview: data.imagePreview,
          fileName: data.fileName,
        },
      });
    }
  };

  // ── Standard vital click ───────────────────────────────────────────────────
  const handleVitalClick = async (vital: VitalReading) => {
    const vitalKey = getVitalKey(vital.type);
    setSelectedVital(vital);
    setIsModalOpen(true);
    setChartLoading(true);
    setChartData(null);

    if (patientID) {
      try {
        const data = await getPatientVitalsCharts(patientID, vitalKey);
        setChartData(data);
      } catch {
        setChartData(null);
      } finally {
        setChartLoading(false);
      }
    } else {
      setChartLoading(false);
    }
  };

  // ── Image vital click ──────────────────────────────────────────────────────
  const handleImageVitalClick = (vital: ImageVitalReading) => {
    setSelectedImageVital(vital);
    setIsImageDetailOpen(true);
  };

  // ── BMI / status helpers (unchanged) ──────────────────────────────────────
  const getBMIThresholds = (ethnicity) => {
    const white = { underweight: 18.5, normal: 25, overweight: 30 };
    const nonWhite = { underweight: 18.5, normal: 23, overweight: 27.5 };
    if (!ethnicity || ethnicity === "" || ethnicity === "0") return white;
    const key = String(ethnicity);
    const label = ethnicity ? ethnicityTypes[ethnicity] : "";
    return key.toLowerCase().includes("white") ||
      label?.toLowerCase().includes("white")
      ? white
      : nonWhite;
  };

  const getBMICategory = (bmi, ethnicity) => {
    const t = getBMIThresholds(ethnicity);
    if (!bmi) return "";
    if (bmi < t.underweight) return "Underweight";
    if (bmi < t.normal) return "Normal weight";
    if (bmi < t.overweight) return "Overweight";
    return "Obese";
  };

  const getVitalStatus = (type: string, value: string, vital: any) => {
    if (type === "consciousness-level") {
      return value === "Alert" ? "normal" : "high";
    }
    if (type === "supplemental-oxygen") {
      return value === "Yes" ? "high" : "normal";
    }

    let numericValue: number;
    if (type === "blood-pressure") {
      numericValue = parseFloat(value.split("/")[0]);
    } else {
      numericValue = parseFloat(value);
    }

    if (isNaN(numericValue)) return "normal";

    if (type === "bmi") {
      return vital?.ethnicity
        ? getBMICategory(numericValue, vital.ethnicity)
        : numericValue < 18.5
          ? "Underweight"
          : numericValue < 25
            ? "Normal weight"
            : numericValue < 30
              ? "Overweight"
              : "Obese";
    }

    // Normalize temperature to °C before comparing
    if (type === "temperature") {
      const unit = vital?.unit || "°C";
      const tempC =
        unit === "°F" || unit === "F"
          ? (numericValue - 32) * (5 / 9)
          : numericValue;
      if (tempC < 36.1) return "low";
      if (tempC > 37.2) return "high";
      return "normal";
    }

    if (type === "blood-sugar") {
      const unit = vital?.unit || "mmol/L";
      const mmol = unit === "mg/dL" ? numericValue / 18 : numericValue;
      if (mmol < 4.0) return "low";
      if (mmol < 5.5) return "normal";
      if (mmol < 7.0) return "prediabetes";
      return "high";
    }

    if (type === "news-pain-score") {
      if (numericValue <= 3) return "normal";
      if (numericValue <= 6) return "moderate";
      return "high";
    }
    if (type === "respiratory-rate") {
      if (numericValue <= 8) return "critical";
      if (numericValue <= 11) return "low";
      if (numericValue <= 20) return "normal";
      if (numericValue <= 24) return "moderate";
      return "critical";
    }
    const thresholds = getVitalThresholds(type);
    if (numericValue < thresholds.low) return "low";
    if (numericValue > thresholds.high) return "high";
    return "normal";
  };

  const getStatusClassesAndLabel = (type: string, status: string) => {
    if (type === "bmi") {
      switch (status) {
        case "Underweight":
          return {
            dot: "bg-yellow-500",
            text: "text-yellow-600",
            label: "Underweight",
          };
        case "Normal weight":
          return {
            dot: "bg-green-500",
            text: "text-green-600",
            label: "Normal weight",
          };
        case "Overweight":
          return {
            dot: "bg-amber-500",
            text: "text-amber-600",
            label: "Overweight",
          };
        case "Obese":
          return { dot: "bg-red-500", text: "text-red-600", label: "Obese" };
      }
    }
    if (status === "moderate")
      return { dot: "bg-amber-500", text: "text-amber-600", label: "Moderate" };
    if (status === "normal")
      return { dot: "bg-green-500", text: "text-green-600", label: "Normal" };
    if (status === "high")
      return { dot: "bg-red-500", text: "text-red-600", label: "High" };
    if (status === "critical")
      return { dot: "bg-red-600", text: "text-red-700", label: "Critical" };
    if (status === "prediabetes")
      return {
        dot: "bg-orange-500",
        text: "text-orange-600",
        label: "Pre-diabetes",
      };
    return { dot: "bg-yellow-500", text: "text-yellow-600", label: "Low" };
  };

  // ── Timestamp helpers (unchanged) ─────────────────────────────────────────
  const getVitalTimestamp = (vital: VitalReading | any): Date => {
    if ("id" in vital && vital.timestamp) return new Date(vital.timestamp);
    return new Date(0);
  };

  const getVitalTimestampFromPatientVitals = (vitalType: string): Date => {
    if (!patientVitals) return new Date(0);
    const map: Record<string, any> = {
      height: patientVitals.height,
      weight: patientVitals.weight,
      bmi: patientVitals.bmi,
      "blood-pressure": patientVitals.bloodPressure,
      "heart-rate": patientVitals.heartRate,
      temperature: patientVitals.temperature,
      "respiratory-rate": patientVitals.respiratoryRate,
      "oxygen-saturation": patientVitals.oxygenSaturation,
      "blood-sugar": patientVitals.glucoseLevel,
      "news-pain-score": patientVitals.painScore,
      circumference: patientVitals.circumference,
    };
    const entry = map[vitalType];
    return entry?.createdOn ? new Date(entry.createdOn) : new Date(0);
  };

  const sortVitalsByRecency = (list: Array<VitalReading | any>) => {
    return [...list].sort((a, b) => {
      const tsA =
        "id" in a
          ? getVitalTimestamp(a)
          : getVitalTimestampFromPatientVitals(a.value);
      const tsB =
        "id" in b
          ? getVitalTimestamp(b)
          : getVitalTimestampFromPatientVitals(b.value);
      return tsB.getTime() - tsA.getTime();
    });
  };

  const getMostRecentVitalTimestamp = (): Date | null => {
    const all: Date[] = [];
    vitals.forEach((v) => v.timestamp && all.push(new Date(v.timestamp)));
    imageVitals.forEach((v) => all.push(new Date(v.timestamp)));
    if (patientVitals) {
      [
        patientVitals.height,
        patientVitals.weight,
        patientVitals.bmi,
        patientVitals.bloodPressure,
        patientVitals.heartRate,
        patientVitals.temperature,
        patientVitals.respiratoryRate,
        patientVitals.glucoseLevel,
        patientVitals.painScore,
        patientVitals.circumference,
      ].forEach((x) => x?.createdOn && all.push(new Date(x.createdOn)));
    }
    if (all.length === 0) return null;
    return all.reduce((latest, cur) => (cur > latest ? cur : latest), all[0]);
  };

  // ── Build ordered vital grid list ────────────────────────────────────────
  const NEWS2_VITAL_TYPES = new Set([
    "news-pain-score",
    "consciousness-level",
    "supplemental-oxygen",
    "oxygen-saturation",
    "blood-sugar",
  ]);
  const filteredVitalTypes = ifNews2Enabled
    ? vitalTypes
    : vitalTypes.filter((vt) => !NEWS2_VITAL_TYPES.has(vt.value));

  const vitalsByType = new Map(vitals.map((v) => [v.type, v]));
  const knownTypes = new Set(filteredVitalTypes.map((vt) => vt.value));
  // Taken standard vitals sorted by recency
  const takenVitals = filteredVitalTypes
    .map((vt) => vitalsByType.get(vt.value))
    .filter(Boolean) as VitalReading[];
  const missingVitals = filteredVitalTypes.filter(
    (vt) => !vitalsByType.has(vt.value),
  );
  const extraVitals = vitals.filter(
    (v) =>
      !knownTypes.has(v.type) &&
      (ifNews2Enabled || !NEWS2_VITAL_TYPES.has(v.type)),
  );
  const sortedTaken = sortVitalsByRecency([...takenVitals, ...extraVitals]);

  // ── Group image vitals by body area (one card per area) ───────────────────
  const imageVitalsByArea = new Map<string, ImageVitalReading[]>();
  imageVitals.forEach((iv) => {
    const existing = imageVitalsByArea.get(iv.label) ?? [];
    imageVitalsByArea.set(iv.label, [...existing, iv]);
  });
  // One representative card per area (most recent thumbnail)
  const imageVitalCards: ImageVitalReading[] = Array.from(
    imageVitalsByArea.entries(),
  ).map(
    ([, imgs]) =>
      imgs.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )[0],
  );

  // ── Order: taken vitals → image cards → missing vitals → upload placeholder
  // Image cards come BEFORE missing vitals so they always appear in the first row
  const IMAGE_PLACEHOLDER_KEY = "__image-upload-placeholder__";
  const allItems: Array<VitalReading | any | { __placeholder: string }> = [
    ...sortedTaken,
    ...imageVitalCards,
    ...missingVitals,
    { __placeholder: IMAGE_PLACEHOLDER_KEY },
  ];

  const firstRowItems = allItems.slice(0, VITALS_PER_ROW);
  const remainingItems = allItems.slice(VITALS_PER_ROW);
  const hasMoreVitals = remainingItems.length > 0;

  // ── Render helpers ─────────────────────────────────────────────────────────

  const getVitalChartData = (vitalType: string) => {
    if (!patientVitals) return [];
    const createTrend = (label, value, createdOn) => {
      const val = Number(value);
      return [
        { name: `${label} (start)`, value: val - 0.5, createdOn },
        { name: `${label} (mid)`, value: val, createdOn },
        { name: `${label} (end)`, value: val + 0.3, createdOn },
      ];
    };
    switch (vitalType) {
      case "blood-pressure":
        return [
          ...createTrend(
            "Systolic",
            patientVitals?.bloodPressure?.systolic?.value,
            patientVitals?.bloodPressure?.systolic?.createdOn,
          ),
          ...createTrend(
            "Diastolic",
            patientVitals?.bloodPressure?.diastolic?.value,
            patientVitals?.bloodPressure?.diastolic?.createdOn,
          ),
        ];
      case "heart-rate":
        return createTrend(
          "Heart Rate",
          patientVitals?.heartRate?.value,
          patientVitals?.heartRate?.createdOn,
        );
      case "temperature":
        return createTrend(
          "Temperature",
          patientVitals?.temperature?.value,
          patientVitals?.temperature?.createdOn,
        );
      case "respiratory-rate":
        return createTrend(
          "Respiratory Rate",
          patientVitals?.respiratoryRate?.value,
          patientVitals?.respiratoryRate?.createdOn,
        );
      default:
        return [];
    }
  };

  // ── Standard vital card (unchanged from original) ─────────────────────────
  const renderVitalCard = (vital: VitalReading) => {
    const IconComponent = getVitalIcon(vital.type);
    const vitalColor = getVitalColor(vital.type);
    const status = getVitalStatus(vital.type, vital.value, vital);
    const chartData = getVitalChartData(vital.type);
    const statusMeta = getStatusClassesAndLabel(vital.type, status);

    return (
      <div
        key={vital.id}
        className={`group rounded-lg ${compact ? "p-2" : "p-4"} border transition-all duration-300 hover:shadow-lg cursor-pointer relative flex flex-col h-full ${
          isDarkMode
            ? "bg-gray-700 border-gray-600 hover:bg-gray-650"
            : "bg-gray-50 border-gray-200 hover:bg-gray-100"
        }`}
      >
        {/* Take Reading Button */}
        {!compact && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
            {onSaveVitals && (
              <VitalsModal
                vitalType={vital.type}
                onSave={onSaveVitals}
                trigger={
                  <button
                    className={`p-1.5 rounded-full text-xs transition-colors ${
                      isDarkMode
                        ? "bg-gray-600 hover:bg-gray-500 text-gray-300"
                        : "bg-white hover:bg-gray-100 text-gray-600 shadow-sm border"
                    }`}
                    title="Take new reading"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                }
              />
            )}
          </div>
        )}

        <div
          onClick={() => handleVitalClick(vital)}
          className="flex flex-col flex-1"
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between ${compact ? "mb-1" : "mb-3"}`}
          >
            <div className="flex items-center gap-2">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: `${vitalColor}20` }}
              >
                <IconComponent
                  className="w-4 h-4"
                  style={{ color: vitalColor }}
                />
              </div>
              <span
                className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {vital.label}
              </span>
            </div>
            <div className={`w-2 h-2 rounded-full ${statusMeta.dot}`} />
          </div>

          {/* Value */}
          <div className={compact ? "mb-1" : "mb-3"}>
            <div className="flex items-baseline gap-1">
              <span
                className={`${compact ? "text-base" : "text-2xl"} font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {vital.value}
              </span>
              <span
                className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {vital.unit}
              </span>
            </div>
            <span className={`text-xs ${statusMeta.text}`}>
              {statusMeta.label}
            </span>
          </div>

          {/* Mini chart */}
          <div className={`${compact ? "h-6" : "h-12"} mb-2`}>
            {vital.historicalData && vital.historicalData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={vitalColor}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 2, fill: vitalColor }}
                  />
                  <Tooltip
                    content={
                      <CustomTooltip
                        vital={vital}
                        active={undefined}
                        payload={undefined}
                        label={undefined}
                      />
                    }
                    cursor={{ stroke: vitalColor, strokeDasharray: "3 3" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div
                className={`h-full flex items-center justify-center border-2 border-dashed rounded ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
              >
                <span
                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  No trend data
                </span>
              </div>
            )}
          </div>

          {/* Timestamp */}
          <div
            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {(() => {
              const ts = getVitalTimestamp(vital);
              if (ts.getTime() === 0) return "No reading";
              const diff = Date.now() - ts.getTime();
              const hours = Math.floor(diff / 3_600_000);
              const mins = Math.floor((diff % 3_600_000) / 60_000);
              if (hours < 1) return mins < 1 ? "Just now" : `${mins}m ago`;
              if (hours < 24) return `${hours}h ago`;
              return ts.toLocaleDateString();
            })()}
          </div>
        </div>
      </div>
    );
  };

  // ── Missing vital card (unchanged from original) ───────────────────────────
  const renderMissingVitalCard = (vitalType: any) => {
    const IconComponent = getVitalIcon(vitalType.value);
    const vitalColor = getVitalColor(vitalType.value);

    return (
      <div
        key={`missing-${vitalType.value}`}
        className={`group rounded-lg ${compact ? "p-2" : "p-4"} border-2 border-dashed transition-all duration-300 hover:shadow-lg flex flex-col h-full ${
          isDarkMode
            ? "bg-gray-800 border-gray-600 hover:bg-gray-750 hover:border-gray-500"
            : "bg-gray-50 border-gray-300 hover:bg-gray-100 hover:border-gray-400"
        }`}
      >
        <div
          className={`flex items-center justify-between ${compact ? "mb-1" : "mb-3"}`}
        >
          <div className="flex items-center gap-2">
            <div
              className="p-2 rounded-lg opacity-50"
              style={{ backgroundColor: `${vitalColor}15` }}
            >
              <IconComponent
                className="w-4 h-4 opacity-75"
                style={{ color: vitalColor }}
              />
            </div>
            <span
              className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {vitalType.label}
            </span>
          </div>
          <div
            className={`w-2 h-2 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-400"}`}
          />
        </div>

        <div className={`${compact ? "mb-1" : "mb-3"} text-center`}>
          <div
            className={`text-lg font-bold ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            --
          </div>
          <span
            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            Not taken
          </span>
        </div>

        <div className={`${compact ? "h-6" : "h-12"} mb-2`}>
          <div
            className={`h-full flex items-center justify-center border border-dashed rounded ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
          >
            <Clock
              className={`w-4 h-4 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
            />
          </div>
        </div>

        <div className="flex items-center justify-center mt-auto">
          {onSaveVitals ? (
            <VitalsModal
              vitalType={vitalType.value}
              onSave={compact ? handleCompactVitalSave : onSaveVitals}
              trigger={
                <div
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border cursor-pointer transition-colors ${
                    isDarkMode
                      ? "border-gray-600 text-gray-400 group-hover:border-gray-500 group-hover:text-gray-300"
                      : "border-gray-300 text-gray-500 group-hover:border-gray-400 group-hover:text-gray-600"
                  }`}
                >
                  <Plus className="w-3 h-3" />
                  <span>Take reading</span>
                </div>
              }
            />
          ) : (
            <div
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-500"}`}
            >
              <Plus className="w-3 h-3" />
              <span>Take reading</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Image vital thumbnail card (NEW) ───────────────────────────────────────
  // const renderImageVitalCard = (vital: ImageVitalReading) => {
  //   const areaImages = imageVitalsByArea.get(vital.label) ?? [];
  //   const imageCount = areaImages.length;

  //   const ts = new Date(vital.timestamp);
  //   const diff = Date.now() - ts.getTime();
  //   const hours = Math.floor(diff / 3_600_000);
  //   const mins = Math.floor((diff % 3_600_000) / 60_000);
  //   const timeAgo =
  //     hours < 1
  //       ? mins < 1
  //         ? "Just now"
  //         : `${mins}m ago`
  //       : hours < 24
  //         ? `${hours}h ago`
  //         : ts.toLocaleDateString();

  //   return (
  //     <div
  //       key={`image-${vital.id}`}
  //       onClick={() => handleImageVitalClick(vital)}
  //       className={`group rounded-lg border transition-all duration-300 hover:shadow-lg cursor-pointer overflow-hidden relative flex flex-col h-full ${
  //         isDarkMode
  //           ? "bg-gray-700 border-gray-600 hover:border-gray-500"
  //           : "bg-gray-50 border-gray-200 hover:border-gray-300"
  //       }`}
  //     >
  //       <div className="px-3 pt-3 pb-1">
  //         <span
  //           className={`text-sm font-medium ${
  //             isDarkMode ? "text-gray-200" : "text-gray-700"
  //           }`}
  //         >
  //           {vital.label}
  //         </span>
  //       </div>
  //       {/* Thumbnail */}
  //       <div className="px-3">
  //         <div className="relative h-32 rounded-md overflow-hidden">
  //           <img
  //             src={vital.imagePreview}
  //             alt={vital.label}
  //             className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
  //           />
  //           {/* Overlay gradient */}
  //           {/* <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" /> */}

  //           {/* Image count badge */}
  //           {imageCount > 1 && (
  //             <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
  //               {imageCount} images
  //             </div>
  //           )}

  //           {/* Video indicator */}
  //           {vital.isVideo && (
  //             <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
  //               Video
  //             </div>
  //           )}

  //           {/* Body area label overlaid on image */}
  //           {/* <div className="absolute bottom-1.5 left-2 right-2">
  //           <span className="text-white text-xs font-semibold drop-shadow-md truncate block">
  //             {vital.label}
  //           </span>
  //         </div> */}
  //         </div>
  //       </div>
  //       {/* Card footer */}
  //       <div className="p-3 flex-1 flex flex-col gap-1">
  //         <div className="flex items-center justify-between mb-1">
  //           <div className="flex items-center gap-1.5">
  //             <div
  //               className={`p-1 rounded ${isDarkMode ? "bg-blue-900/40" : "bg-blue-100"}`}
  //             >
  //               <ImageIconLucide
  //                 className={`w-3 h-3 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
  //               />
  //             </div>
  //             <span
  //               className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
  //             >
  //               Image Vital
  //             </span>
  //           </div>
  //           {/* Green dot — "has reading" */}
  //           <div className="w-2 h-2 rounded-full bg-green-500" />
  //         </div>

  //         <div
  //           className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
  //         >
  //           {timeAgo}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const renderImageVitalCard = (vital: ImageVitalReading) => {
    const areaImages = imageVitalsByArea.get(vital.label) ?? [];
    const imageCount = areaImages.length;

    const ts = new Date(vital.timestamp);
    const diff = Date.now() - ts.getTime();
    const hours = Math.floor(diff / 3_600_000);
    const mins = Math.floor((diff % 3_600_000) / 60_000);
    const timeAgo =
      hours < 1
        ? mins < 1
          ? "Just now"
          : `${mins}m ago`
        : hours < 24
          ? `${hours}h ago`
          : ts.toLocaleDateString();

    return (
      <div
        key={`image-${vital.id}`}
        onClick={() => handleImageVitalClick(vital)}
        className={`group rounded-lg ${compact ? "p-2" : "p-4"} border transition-all duration-300 hover:shadow-lg cursor-pointer relative flex flex-col h-full ${
          isDarkMode
            ? "bg-gray-700 border-gray-600 hover:bg-gray-650"
            : "bg-gray-50 border-gray-200 hover:bg-gray-100"
        }`}
      >
        {/* Header — icon + label + image count + status dot */}
        <div
          className={`flex items-center justify-between ${compact ? "mb-1" : "mb-3"}`}
        >
          <div className="flex items-center gap-2">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: "#3b82f620" }}
            >
              <ImageIconLucide
                className="w-4 h-4"
                style={{ color: "#3b82f6" }}
              />
            </div>
            <div className="flex flex-col">
              <span
                className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {vital.label}
              </span>
              {imageCount > 1 && (
                <span
                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  {imageCount} {vital.isVideo ? "videos" : "images"}
                </span>
              )}
            </div>
          </div>
          <div className="w-2 h-2 rounded-full bg-green-500" />
        </div>

        {/* Thumbnail */}
        <div
          className={`${compact ? "h-16" : "h-32"} mb-2 group-hover:opacity-90 transition-opacity`}
        >
          <div className="relative h-full w-full rounded overflow-hidden">
            <img
              src={vital.imagePreview}
              alt={vital.label}
              className={`w-full h-full object-contain ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all" />
            {/* Video badge */}
            {vital.isVideo && (
              <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                Video
              </div>
            )}
          </div>
        </div>

        {/* Timestamp */}
        <div
          className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
        >
          {timeAgo}
        </div>
      </div>
    );
  };

  // ── Add-image placeholder card (NEW) ──────────────────────────────────────
  const renderAddImageVitalCard = () => (
    <UploadImageModal
      mode="vitals"
      trigger={
        <div
          className={`group rounded-lg ${compact ? "p-2" : "p-4"} border-2 border-dashed transition-all duration-300 hover:shadow-lg cursor-pointer flex flex-col h-full ${
            isDarkMode
              ? "bg-gray-800 border-gray-600 hover:bg-gray-750 hover:border-blue-600"
              : "bg-gray-50 border-gray-300 hover:bg-blue-50/40 hover:border-blue-400"
          }`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between ${compact ? "mb-1" : "mb-3"}`}
          >
            <div className="flex items-center gap-2">
              <div
                className={`p-2 rounded-lg ${isDarkMode ? "bg-blue-900/30" : "bg-blue-100"}`}
              >
                <ImageIconLucide
                  className={`w-4 h-4 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                />
              </div>
              <span
                className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Image Vital
              </span>
            </div>
            <div
              className={`w-2 h-2 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-400"}`}
            />
          </div>

          {/* Empty state */}
          <div className={`${compact ? "mb-1" : "mb-3"} text-center`}>
            <div
              className={`text-lg font-bold ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              --
            </div>
            <span
              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Not taken
            </span>
          </div>

          {/* Icon placeholder */}
          <div className={`${compact ? "h-6" : "h-12"} mb-2`}>
            <div
              className={`h-full flex items-center justify-center border border-dashed rounded ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
            >
              <ImageIconLucide
                className={`w-4 h-4 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
              />
            </div>
          </div>

          {/* CTA */}
          <div className="flex items-center justify-center mt-auto">
            <div
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors ${
                isDarkMode
                  ? "border-gray-600 text-gray-400 group-hover:border-blue-600 group-hover:text-blue-400"
                  : "border-gray-300 text-gray-500 group-hover:border-blue-400 group-hover:text-blue-600"
              }`}
            >
              <Plus className="w-3 h-3" />
              <span>Upload image</span>
            </div>
          </div>
        </div>
      }
      onSave={handleSaveImageVital}
      patientEmail={selectedPatientDetails?.email}
      patientName={selectedPatientDetails?.patientName}
      // Pass empty strings for now — API work later
      consultationId={patient?.consultation?.consultationId ?? ""}
      patientId={selectedPatientDetails?.patientID}
    />
  );

  // ── Render a single grid item (dispatch to correct renderer) ──────────────
  const renderGridItem = (item: any, index: number) => {
    // Add-image placeholder
    if (item?.__placeholder === IMAGE_PLACEHOLDER_KEY) {
      return (
        <React.Fragment key="image-upload-placeholder">
          {renderAddImageVitalCard()}
        </React.Fragment>
      );
    }
    // Image vital thumbnail
    if (item?.type === "image") {
      return renderImageVitalCard(item as ImageVitalReading);
    }
    // Taken standard vital
    if ("id" in item) {
      return renderVitalCard(item as VitalReading);
    }
    // Missing standard vital
    return renderMissingVitalCard(item);
  };

  // ── Derive "all images for selected body area" for detail modal ────────────
  const selectedAreaImages = selectedImageVital
    ? (imageVitalsByArea.get(selectedImageVital.label) ?? []).map((v) => ({
        id: v.id,
        label: v.label,
        description: v.description,
        imagePreview: v.imagePreview,
        timestamp: v.timestamp,
        fileName: v.fileName,
        isVideo: v.isVideo,
      }))
    : [];

  const mostRecentTimestamp = getMostRecentVitalTimestamp();

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <div
      className={`rounded-xl ${compact ? "p-3" : "p-6"} mb-6 transition-colors duration-300 ${isDarkMode ? "bg-gray-800" : "bg-white"} ${!compact ? (isDarkMode ? "border border-gray-700" : "border border-gray-200") : ""}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Activity
            className={`w-5 h-5 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          {compact ? (
            <h3
              className={`text-md font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Vitals
            </h3>
          ) : (
            <h3
              className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Patient Vitals
            </h3>
          )}
        </div>
        <div className="flex items-center gap-3">
          {ifNews2Enabled && (
            <NEWS2ScorePanelCompact vitals={vitals} isDarkMode={isDarkMode} />
          )}
          {compact ? (
            <div className="header-title">
              <div
                className="add-vital-btn"
                onClick={() => {
                  setCurrentOpenVitalsModal({
                    isEdit: false,
                    vital: null,
                    vitalType: null,
                  });
                }}
              >
                <GoPlusCircle />
                <p>Add Vitals</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <TrendingUp
                className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
              <span
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Last 7 days
              </span>
            </div>
          )}
        </div>
      </div>

      {/* First row */}
      <div
        className={
          compact
            ? "grid grid-cols-2 gap-2 items-stretch"
            : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 items-stretch"
        }
      >
        {firstRowItems.map((item, i) => renderGridItem(item, i))}
      </div>

      {/* Expandable rows */}
      {hasMoreVitals && (
        <>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div
              className={
                compact
                  ? "grid grid-cols-2 gap-2 mt-2 items-stretch"
                  : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-4 items-stretch"
              }
            >
              {remainingItems.map((item, i) => renderGridItem(item, i))}
            </div>
          </div>

          <div className="flex justify-center mt-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
                isDarkMode
                  ? "bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200"
              }`}
            >
              <span className="text-sm font-medium">
                {isExpanded
                  ? "Show Less"
                  : `Show ${remainingItems.length} More`}
              </span>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </>
      )}

      {/* Footer: last updated */}
      <div
        className={`mt-4 pt-4 border-t ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
      >
        <div className="flex items-center justify-between text-sm">
          <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Last updated:
          </span>
          <span
            className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {mostRecentTimestamp
              ? `${mostRecentTimestamp.toLocaleDateString()} at ${mostRecentTimestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
              : "No readings"}
          </span>
        </div>
      </div>

      {/* Standard vital detail modal */}
      <VitalsDetailModal
        isOpen={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) {
            setChartData(null);
            setChartLoading(false);
          }
        }}
        vital={selectedVital}
        isDarkMode={isDarkMode}
        loading={chartLoading}
        chartData={chartData}
      />

      {/* Image vital detail modal */}
      <ImageVitalsDetailModal
        isOpen={isImageDetailOpen}
        onOpenChange={setIsImageDetailOpen}
        imageVitals={selectedAreaImages}
        isDarkMode={isDarkMode}
        onSaveVitals={handleSaveImageVital}
        bodyAreaLabel={selectedImageVital?.label}
        patientId={selectedPatientDetails?.patientID}
      />
    </div>
  );
}
